import { Button } from "@/components/ui/button";
import {
  Pencil,
  Trash2,
  Loader2,
  ChevronUp,
  MessageCirclePlus,
  ArrowLeftFromLine,
  ArrowRightFromLine,
  ChevronDown,
  History,
} from "lucide-react";
import { useState, useEffect } from "react";
import { ConversationChunk } from "../data/types";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { MessageAvatar } from "./MessageAvatar";
import { MessageBubbleInput } from "./MessageBubbleInput";
import { parseContent } from "../data/helpers";
import { PurpleTypingIndicatorComponent } from "./purple-typing-indicator";

interface ChunkProps {
  chunk: ConversationChunk;
  isLastChunk: boolean;
  userName: string;
  targetName: string;
  isLoading?: boolean;
  onEditMessage: (
    chunkId: string,
    messageIndex: number,
    newContent: string,
    isNewMessage?: boolean
  ) => void;
  onDeleteMessage: (chunkId: string, messageIndex: number) => void;
  onDeleteAIChat?: () => void;
  defaultOpen?: boolean;
  onMoveMessage: (chunkId: string, messageIndex: number) => void;
  messageRef?: React.RefObject<HTMLDivElement>;
}

export function Chunk({
  chunk,
  isLastChunk,
  userName,
  targetName,
  isLoading = false,
  onEditMessage,
  onDeleteMessage,
  onDeleteAIChat,
  defaultOpen = false,
  onMoveMessage,
  messageRef,
}: ChunkProps) {
  const [editingMessageIndex, setEditingMessageIndex] = useState<number | null>(
    null
  );
  const [openAccordion, setOpenAccordion] = useState<string | undefined>(
    defaultOpen ? chunk.id : undefined
  );
  const [addingMessageAfterIndex, setAddingMessageAfterIndex] = useState<
    number | null
  >(null);
  const [visibleMessageCount, setVisibleMessageCount] = useState<number>(20);

  useEffect(() => {
    if (
      defaultOpen ||
      (isLastChunk && chunk.aiChat?.messages.length) ||
      (isLastChunk && isLoading)
    ) {
      setOpenAccordion(chunk.id);
    }
  }, [
    defaultOpen,
    chunk.id,
    isLastChunk,
    chunk.aiChat?.messages.length,
    isLoading,
  ]);

  const handleEditMessage = (messageIndex: number) => {
    setEditingMessageIndex(messageIndex);
  };

  const handleAddMessage = (messageIndex: number) => {
    const currentMessage = chunk.conversationMessages[messageIndex];

    // Create a temporary empty message with the same sender as the current message
    const newMessage = {
      id: crypto.randomUUID(),
      sender: currentMessage.sender,
      content: "",
    };

    // Insert new empty message after the current one
    onEditMessage(chunk.id, messageIndex + 1, "", true);
    setAddingMessageAfterIndex(messageIndex);
    setEditingMessageIndex(messageIndex + 1);
  };

  const handleSaveEdit = (messageIndex: number, newContent: string) => {
    if (newContent.trim() === "") {
      // If the message is empty, delete it
      onDeleteMessage(chunk.id, messageIndex);
    } else {
      // Save the edited message
      onEditMessage(chunk.id, messageIndex, newContent.trim(), false);
    }
    setEditingMessageIndex(null);
    setAddingMessageAfterIndex(null);
  };

  const handleCancelEdit = () => {
    // Only delete the message if we were adding a new one
    if (addingMessageAfterIndex !== null && editingMessageIndex !== null) {
      onDeleteMessage(chunk.id, editingMessageIndex);
    }
    setEditingMessageIndex(null);
    setAddingMessageAfterIndex(null);
  };

  const handleDeleteMessage = async (chunkId: string, index: number) => {
    setEditingMessageIndex(null);
    setAddingMessageAfterIndex(null);
    await onDeleteMessage(chunkId, index);
  };

  const handleLoadMore = () => {
    setVisibleMessageCount((prev) => prev + 20);
  };

  if (
    chunk.conversationMessages.length === 0 &&
    !chunk.aiChat?.messages.length &&
    !isLoading
  ) {
    return null;
  }

  return (
    <div className={`${isLastChunk ? "mb-6" : "mb-6"}`}>
      {chunk.conversationMessages.length > 0 && (
        <div className="mb-6">
          {chunk.conversationMessages.map((message, index) => (
            <div
              key={message.id}
              className={`flex ${
                message.sender === "user" ? "justify-end" : "justify-start"
              } group mb-2 sm:mb-4 relative`}
            >
              {editingMessageIndex === index ? (
                <MessageBubbleInput
                  isUser={message.sender === "user"}
                  isEditing={true}
                  userName={userName}
                  targetName={targetName}
                  onClose={handleCancelEdit}
                  onSave={(newMessage) => handleSaveEdit(index, newMessage)}
                  initialMessage={message.content}
                  onDelete={() => handleDeleteMessage(chunk.id, index)}
                  onMove={() => onMoveMessage(chunk.id, index)}
                />
              ) : (
                <div
                  className={`flex items-start gap-2 sm:max-w-[80%] max-w-[100%] flex-shrink ${
                    message.sender === "user" ? "flex-row-reverse" : "flex-row"
                  }`}
                >
                  <MessageAvatar
                    isUser={message.sender === "user"}
                    userName={userName}
                    targetName={targetName}
                  />
                  <div className="">
                    <div
                      className={`${
                        message.sender === "user"
                          ? "bg-gradient-to-b from-gray-700  from-50%  to-gray-900 text-white"
                          : "bg-gradient-to-b from-gray-200  from-50%  to-gray-300 text-gray-900 "
                      }  py-2 px-4 rounded-3xl`}
                    >
                      <p className="whitespace-pre-wrap text-base">
                        {message.content}
                      </p>
                    </div>
                  </div>
                  {editingMessageIndex !== index ? (
                    <div
                      className={`flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity pt-1 sm:pt-0 ${
                        message.sender === "user"
                          ? "flex-row-reverse"
                          : "flex-row"
                      }`}
                    >
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEditMessage(index)}
                        className="h-8 w-8 focus:ring-0 focus:ring-offset-0 text-gray-500 hover:text-gray-700 hover:bg-gray-200"
                      >
                        <Pencil className="h-4 w-4" />
                        <span className="sr-only">Edit message</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleAddMessage(index)}
                        className="h-8 w-8 focus:ring-0 focus:ring-offset-0 text-gray-500 hover:text-gray-700 hover:bg-gray-200"
                      >
                        <MessageCirclePlus className="h-4 w-4" />
                        <span className="sr-only">Add message</span>
                      </Button>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {(chunk.aiChat || (isLastChunk && isLoading)) && (
        <Accordion
          type="single"
          collapsible
          className={`w-full rounded-xl border  bg-white  ${
            isLastChunk ? "  border-gray-200 " : "border-gray-200"
          }`}
          value={openAccordion}
          onValueChange={setOpenAccordion}
        >
          <AccordionItem value={chunk.id} className="border-none">
            <AccordionTrigger
              className={`px-4 focus:ring-0 focus:ring-offset-0 bg-white  py-4 rounded-xl hover:text-purple-600  ${
                isLastChunk ? "text-purple-600" : "text-gray-500"
              }`}
            >
              {/* {isLastChunk ? "AI Wingman" : "AI Wingman History"} */}
              Options AI Chat
            </AccordionTrigger>
            <AccordionContent className="px-4 py-2 bg-white rounded-b-xl">
              {chunk.aiChat &&
                chunk.aiChat.messages.length > visibleMessageCount && (
                  <div className="flex justify-center mt-2 mb-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleLoadMore}
                      // className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 border-purple-200"
                    >
                      <History className="h-4 w-4 mr-2" />
                      Load past messages
                    </Button>
                  </div>
                )}
              {chunk.aiChat?.messages
                .filter((message) => message.role !== "summary")
                .slice(-visibleMessageCount)
                .map((message, index) => (
                  <div
                    key={`${chunk.id}-ai-${index}`}
                    ref={message.role === "user" ? messageRef : undefined}
                    className={`flex ${
                      message.role === "user" ? "justify-end" : "justify-start"
                    } mb-2`}
                  >
                    <div
                      className={`my-2 text-black rounded-xl ${
                        message.role === "user"
                          ? "ml-auto bg-purple-100 px-4 p-3 max-w-[80%] " +
                            (message.type === "replies-request" ||
                            message.type === "analysis-request"
                              ? "font-medium italic text-purple-600 text-sm"
                              : "")
                          : "mr-auto "
                      }`}
                    >
                      {parseContent(message.content)}
                    </div>
                  </div>
                ))}
              {isLastChunk && isLoading && (
                <div className="flex justify-start my-4">
                  <div className="mr-auto text-black">
                    <PurpleTypingIndicatorComponent />
                  </div>
                </div>
              )}
              <div className="flex flex-col gap-2 mt-4">
                {/* {isLastChunk &&
                  onDeleteAIChat &&
                  chunk.aiChat?.messages.length > 1 && (
                    <Button
                      onClick={onDeleteAIChat}
                      variant="ghost"
                      className="text-red-500 hover:text-red-600 hover:bg-red-50 w-full"
                    >
                      Delete AI Chat (temporary - for testing)
                    </Button>
                  )} */}
                <AccordionTrigger className="w-full text-center text-gray-500 hover:text-purple-600 flex items-center justify-center gap-1 py-2 text-sm">
                  Close
                </AccordionTrigger>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
    </div>
  );
}
